import type { CarouselApi } from '~/components/ui/carousel';

interface UseShortsPageLifecycleOptions {
	isOpen: Ref<boolean>;
	initialIndex: Ref<number>;
	initialMuted: Ref<boolean>;
	showNavigation: Ref<boolean>;
	emblaApi: Ref<CarouselApi | null>;
	currentIndex: Ref<number>;
	isScrollLocked: Ref<boolean>;
	resetCurrentVideoState: (resetFirstVideoLoaded?: boolean) => void;
	setGlobalMuted: (isMuted: boolean) => void;
	prefetchUpcoming: (startIndex: number) => void;
	abortCurrentPrefetch: () => void;
	cleanupPrefetch: () => void;
	notifyExternalMedia: () => void;
	onOpen?: () => void;
	onClose?: () => void;
}

export function useShortsPageLifecycle(options: UseShortsPageLifecycleOptions) {
	const unwatchIsOpen = watch(
		options.isOpen,
		open => {
			if (open) {
				options.currentIndex.value = options.initialIndex.value;
				options.resetCurrentVideoState(true);
				options.notifyExternalMedia();
				options.onOpen?.();

				if (!options.showNavigation.value) {
					options.isScrollLocked.value = true;
				}

				nextTick(() => {
					if (options.emblaApi.value && options.initialIndex.value > 0) {
						options.emblaApi.value.scrollTo(options.initialIndex.value, true);
					}

					options.prefetchUpcoming(options.initialIndex.value);
				});
				return;
			}

			options.resetCurrentVideoState(true);
			options.abortCurrentPrefetch();
			options.onClose?.();

			if (!options.showNavigation.value) {
				options.isScrollLocked.value = false;
			}
		},
		{ immediate: true }
	);

	const unwatchInitialIndex = watch(
		options.initialIndex,
		newIndex => {
			if (
				options.isOpen.value &&
				options.emblaApi.value &&
				newIndex !== options.currentIndex.value
			) {
				options.currentIndex.value = newIndex;
				options.resetCurrentVideoState();
				options.emblaApi.value.scrollTo(newIndex, true);
				options.prefetchUpcoming(newIndex);
			}
		},
		{ flush: 'post' }
	);

	const unwatchInitialMuted = watch(options.initialMuted, isMuted => {
		options.setGlobalMuted(isMuted);
	});

	onBeforeUnmount(() => {
		unwatchIsOpen();
		unwatchInitialIndex();
		unwatchInitialMuted();
		options.cleanupPrefetch();
		options.isScrollLocked.value = false;
	});
}
